import { z } from 'zod';
import { zCell, zGameCode, zSeatId, zTeamId } from './enums.js';
/**
 * Join, seat claim, and session identity (§3.1–3.3).
 *
 * There are no accounts. Access is three secrets per game — a game code that is
 * not a secret, a per-team password, and a GM password — exchanged for two
 * short-lived tokens:
 *
 *   - a **session token**, which proves you know a team's password and lets you
 *     see the seat roster and claim a seat;
 *   - a **seat token**, which is the credential for everything else and carries
 *     the seat's epoch so a force-release invalidates it (decision M2-D8).
 *
 * Both are `Authorization: Bearer`, never cookies (§2.1): cross-site cookies need
 * `SameSite=None; Secure`, are increasingly restricted, and would drag CSRF into
 * a design that otherwise has none.
 */
const strict = (shape) => z.object(shape).strict();
export const zJoinRole = z.enum(['BLUFOR', 'REDFOR', 'GM']);
/**
 * How a scenario presents one side (§10.2's `sides` block).
 *
 * `BLUFOR`/`REDFOR`/`GM` are the protocol's; what a *game* calls them is the
 * scenario's, because the pairing is — Indo-Pacific Command against a Red
 * theatre command in one exercise, something else entirely in the next. The
 * server resolves the scenario's overrides over `DEFAULT_SIDE_BRANDING` and
 * sends the complete set, so a client never merges anything.
 */
export const zSideBranding = strict({
    /** The card's title: "BLUFOR", "COALITION", "Control". */
    name: z.string().min(1).max(60),
    /** The line beneath it: "U.S. Indo-Pacific Command". */
    identity: z.string().min(1).max(60),
    /**
     * The unit standing behind each of this side's cells, keyed by `SeatId` —
     * `{ "MW": "2nd Space Warning Squadron" }`.
     *
     * A record of plain strings rather than a per-seat object schema, and
     * deliberately so: it is **partial**, because a scenario need not name every
     * cell, and a schema claiming all nine keys exist would be a type that lies
     * about half the scenarios in the repository. The server drops keys that are
     * not seat ids before this goes out, so a reader may trust the keys without
     * trusting that any given one is present. Use `unitOf` rather than indexing.
     */
    cells: z.record(z.string(), z.string().min(1).max(60)).readonly(),
}).readonly();
/** The unit behind one cell, or null where the scenario did not name one. */
export function unitOf(branding, cell) {
    return branding.cells[cell] ?? null;
}
export const zSideBrandingSet = strict({
    BLUFOR: zSideBranding,
    REDFOR: zSideBranding,
    GM: zSideBranding,
}).readonly();
/**
 * What a side is called when the scenario does not say.
 *
 * Here rather than in either package that uses it, because **both** need it and
 * they must not disagree: the server merges a scenario's overrides onto this,
 * and the entry screen — which runs before any credential has been accepted and
 * so cannot know the scenario — draws exactly this. The moment they are two
 * lists, entry's closing match-cut into seat select lands on a different name.
 */
export const DEFAULT_SIDE_BRANDING = zSideBrandingSet.parse({
    // No default units. A cell's unit is a real or invented formation — 4 SOPS,
    // PLASSF — and inventing one for a scenario that did not ask would put words
    // in an author's mouth. Unnamed simply shows no unit line.
    BLUFOR: { name: 'BLUFOR', identity: 'U.S. Indo-Pacific Command', cells: {} },
    REDFOR: { name: 'REDFOR', identity: 'Red Theater Command', cells: {} },
    GM: { name: 'Control', identity: 'Exercise Control', cells: {} },
});
/**
 * Seats free and seats total for one side.
 *
 * "Open" counts seats nobody holds, not seats nobody is *connected* to — a
 * player who dropped still owns their seat through the grace window, and
 * offering it to a queue would be a lie the claim call then refuses.
 */
export const zSideCapacity = strict({
    open: z.number().int().min(0),
    total: z.number().int().min(0),
}).readonly();
/**
 * What an unauthenticated caller may know about a game — `GET /api/games/{code}/capacity`.
 *
 * This is a **deliberate, narrow reversal** of §3.3's "nothing about a game is
 * visible before the passphrase". Two things pushed it: a seat count is a
 * queue-management aid rather than a fog-of-war fact — knowing REDFOR has two
 * chairs left tells you nothing about the exercise — and the side-select screen
 * is otherwise obliged to draw a permanently blank statistic, which reads as
 * broken to everyone who did not write the rule.
 *
 * What it does concede is a **"does this code exist" oracle**: a 404 here
 * answers a question join deliberately would not. That is judged acceptable
 * because the game code was never the secret (entry.md: "the code isn't a
 * secret", and it travels in a `?code=` deep link); the per-side passphrase is,
 * and it keeps its own far tighter bucket. The scenario name rides along too —
 * it isn't sensitive, and side-select needs it to resolve the scenario's own
 * crest art before the passphrase clears, same as everything past it does.
 */
export const zGameCapacity = strict({
    code: zGameCode,
    /** So side-select can name the sides as this scenario does, before the passphrase. */
    sides: zSideBrandingSet,
    /** So side-select and the passphrase panel can resolve the scenario's own art. */
    scenario: z.string(),
    capacity: strict({
        BLUFOR: zSideCapacity,
        REDFOR: zSideCapacity,
        GM: zSideCapacity,
    }).readonly(),
}).readonly();
export const zJoinRequest = strict({
    role: zJoinRole,
    /**
     * A four-word diceware phrase by default (§3.4) — memorable enough to say out
     * loud in a briefing room. Bounded so a megabyte of "password" costs nothing.
     */
    password: z.string().min(1).max(256),
    displayName: z.string().trim().min(1).max(40).optional(),
});
export const zSeatSummary = strict({
    cell: zSeatId,
    team: z.union([zTeamId, z.literal('GM')]),
    claimed: z.boolean(),
    /**
     * A socket is attached **right now**. False for the whole of seat select, on
     * every seat: the socket needs the seat token that screen exists to obtain.
     */
    connected: z.boolean(),
    /**
     * Has this holder ever had a socket, at any point since they claimed.
     *
     * Without it `claimed && !connected` is two different situations wearing one
     * flag — a player who dropped mid-turn and is inside the 120 s grace window,
     * and a player who has claimed a seat and is still reading the briefing before
     * entering. The roster called both of them `reconnecting`, which meant every
     * peer on the seat-select screen read as a dropped connection, in amber,
     * permanently. Reset on claim, so it describes the current holder.
     */
    everConnected: z.boolean(),
    displayName: z.string().nullable(),
    /** Seconds since the current holder connected — §3.2's 409 detail. */
    heldForMs: z.number().int().nullable(),
}).readonly();
export const zJoinResponse = strict({
    sessionToken: z.string(),
    expiresAtMs: z.number().int(),
    gameId: z.string().uuid(),
    code: zGameCode,
    role: zJoinRole,
    seats: z.array(zSeatSummary).readonly(),
    /**
     * The scenario's name and its side branding. Both are post-passphrase
     * knowledge — nothing about a game is disclosed before it (§3.3) — and both
     * arrive here rather than a round trip later because the screen that accepted
     * the passphrase is already drawing the side it was given. The name is also
     * what resolves the scenario's art, so the crest the entry screen hands off
     * with is the crest seat select opens on.
     */
    scenario: z.string(),
    sides: zSideBrandingSet,
}).readonly();
export const zSeatClaimRequest = strict({
    displayName: z.string().trim().min(1).max(40).optional(),
    /**
     * Self-reclaim from a new device (§3.2). Presenting the previous seat token
     * releases the old holder and issues a fresh token at a bumped epoch, so the
     * abandoned tab stops working immediately rather than fighting for the seat.
     */
    reclaimToken: z.string().optional(),
});
export const zSeatClaimResponse = strict({
    seatToken: z.string(),
    expiresAtMs: z.number().int(),
    gameId: z.string().uuid(),
    seat: strict({
        cell: zSeatId,
        team: z.union([zTeamId, z.literal('GM')]),
        epoch: z.number().int(),
    }).readonly(),
}).readonly();
/** What `session.hello` reports once a seat token has been presented. */
export const zSeatIdentity = strict({
    cell: zSeatId,
    team: z.union([zTeamId, z.literal('GM')]),
    isGm: z.boolean(),
    epoch: z.number().int(),
    displayName: z.string().nullable(),
}).readonly();
/** Response to either release route — the roster, refreshed. */
export const zSeatReleaseResponse = strict({
    seats: z.array(zSeatSummary).readonly(),
}).readonly();
/** GM-only: force a seat back to the pool (§3.2), always with a reason for the audit log. */
export const zForceReleaseRequest = strict({
    cell: zSeatId,
    team: z.union([zTeamId, z.literal('GM')]),
    reason: z.string().trim().min(1).max(200),
});
export { zCell };
//# sourceMappingURL=session.js.map